import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Heading,
  Stack,
  Flex,
  Text,
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Image,
  Spinner,
  Alert,
} from '@chakra-ui/react';
import { LuHouse } from 'react-icons/lu';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  InputBox,
  DatePickerBox,
  SelectBox,
} from '../../components/shared/fields';
import ButtonBox from '../../components/shared/buttons/ButtonBox';
import { GridCol, GridRow } from '../../components/shared/grids';
import BreadCrumbBox from '../../components/shared/BreadCrumbBox';
import { useColorModeValue } from '../../components/ui/color-mode';
import DialogBox from '../../components/shared/dialogs/DialogBox';
import { dateToTimeStamp } from '../../utils/constants';
import { darkMode, lightMode } from '../../theme/config';
import { closeDialog, openDialog } from '../../store/dialogSlice';
import ImageUploader from '../../components/shared/ImageUploader';
import ImagePreviewDialog from '../../components/shared/dialogs/ImagePreviewDialog';
import { listPath, uploadPath } from '../../utils/constants/insightsPath';
import { resetListData } from '../../store/listSlice';
import { clearProductDetail, clearProductData } from '../../store/productSlice';
import {
  clearFiles,
  clearImageChanges,
} from '../../store/filesSlice';

import styles from './productadd.module.css';

const ProductAdd = () => {
  const borderColor = useColorModeValue(
    lightMode.containerBorderColor,
    darkMode.containerBorderColor,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cancelFormModalOpen = useSelector((state) => state.dialogs.addProduct);
  const userId = useSelector((state) => state.user.userId);
  const id = useSelector((state) => state.product.id);

  // Image implementation from complaint component
  const { images, imagesLoading, imagesError } = useSelector(
    (state) => state.product,
  );
  const fileData = useSelector(
    (state) =>
      state.files[uploadPath.PRODUCT.PRODUCT_UPLOAD] || {
        files: [],
        existingImages: [],
        removedImageUrls: [],
      },
  );
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

  const imageFiles = useSelector(
    (state) => state.files[uploadPath.PRODUCT.PRODUCT_UPLOAD] || [],
  );
  const [imageError, setImageError] = useState(null);
  const [serialError, setSerialError] = useState('');

  const { productCategories, productSubcategories } = useSelector(
    (state) =>
      state.categories || {
        productCategories: [],
        productSubcategories: {},
        productModelMap: {},
      },
  );
  const productData = useSelector(
    (state) =>
      state.product || {
        title: '',
        productId: '',
        productName: '',
        categoryObject: {},
        subCategoryObject: {},
        registrationFlow: 'CRM',
        modelObject: {},
        serialNo: '',
        purchaseDate: '',
        warrantyDurationInDays: '',
        isEdit: false,
      },
  );

  const productList = useSelector(
    (state) => state.list[listPath.PRODUCT] || { rows: [] },
  );
  const serialList = productList.rows.map((list) => list.serialNo);

  const customer = useSelector((state) => state.user);
  const { models } = useSelector((state) => state.models);
  // console.log(models)
  // Color mode values for image section
  const cardBg = useColorModeValue('white', 'gray.800');
  const imageSectionBg = useColorModeValue('purple.50', 'purple.950');
  const imageSectionTextColor = useColorModeValue('purple.800', 'purple.200');

  // Image handlers
  const handleImageClick = (image) => {
    setSelectedImage(image);
    setIsImageDialogOpen(true);
  };

  // Effects for image management
  useEffect(() => {
    if (productData?.productId || productData?.id) {
      dispatch({
        type: 'GET_PRODUCT_IMAGES',
        payload: {
          productId: productData.productId || productData.id,
        },
      });
    }
  }, [productData?.productId, productData?.id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearFiles({ key: uploadPath.PRODUCT.PRODUCT_UPLOAD }));
      dispatch(clearProductData());
    };
  }, [dispatch]);

  // Clear files when loading the form for adding a new product (not edit mode)
  useEffect(() => {
    dispatch(clearFiles({ key: uploadPath.PRODUCT.PRODUCT_UPLOAD }));
    dispatch(clearImageChanges({ key: uploadPath.PRODUCT.PRODUCT_UPLOAD }));
  }, [dispatch]);

  // Error Handling using zod and react hook form
  const schema = z.object({
    productName: z.string().optional(),
    productCategory: z.array(z.string(), { required_error: "Product Category is required" }).nonempty("Product Category is required"),
    productSubCategory: z.array(z.string(), { required_error: "Product Sub Category is required" }).nonempty("Product Sub Category is required"),
    productModel: z.array(z.string()).optional(),
    registrationFlow: z.string().nonempty("Registration Flow is required"),
    model: z.array(z.string()).nonempty("Model is required"),
    serialNo: z.string().min(1, "Serial No is required"),
    purchaseDate: z.date({ required_error: "Purchase Date is required", invalid_type_error: "Invalid Date" }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    setValue,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      productName: productData?.productName || '',
      serialNo: productData?.serialNo || '',
      purchaseDate: productData?.purchaseDate
        ? new Date(productData.purchaseDate)
        : null,
      registrationFlow: productData.registrationFlow || 'CRM',
      productCategory: productData.categoryObject?.value
        ? [productData.categoryObject.value]
        : [],
      productSubCategory: productData.subCategoryObject?.value
        ? [productData.subCategoryObject.value]
        : [],
      productModel: productData.modelObject?.value
        ? [productData.modelObject.value]
        : [],
      model: (() => {
        const matchedModel = models?.find((model) => model.value === productData.skuId);
        return matchedModel ? [matchedModel.value] : [];
      })(),
    },
  });

  const selectedCategory = useWatch({
    control,
    name: 'productCategory',
  });

  const selectedSubCategory = useWatch({
    control,
    name: 'productSubCategory',
  });

  const selectedModelValue = useWatch({
    control,
    name: 'model',
  });

  const selectedModel = models?.find((model) => model.value === selectedModelValue?.[0]);
  // console.log(selectedModel)
  const firstLoadRef = useRef(true);
  const categoryChangeRef = useRef(false);
  const subCategoryChangeRef = useRef(false);

  useEffect(() => {
    // Skip on first load or if this is the initial value
    if (firstLoadRef.current || !categoryChangeRef.current) {
      categoryChangeRef.current = true;
      return;
    }

    // Reset subcategory and model when category changes
    setValue('productSubCategory', []);
    setValue('productModel', []);
  }, [selectedCategory, setValue]);

  useEffect(() => {
    // Skip on first load or if this is the initial value
    if (firstLoadRef.current || !subCategoryChangeRef.current) {
      subCategoryChangeRef.current = true;
      return;
    }

    // Reset model when subcategory changes
    setValue('productModel', []);
  }, [selectedSubCategory, setValue]);

  useEffect(() => {
    // Mark first load as complete after initial render
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (id && id !== '') {
      dispatch(resetListData(listPath.PRODUCT));
      dispatch(clearFiles({ key: uploadPath.PRODUCT.PRODUCT_UPLOAD }));
      dispatch(clearImageChanges({ key: uploadPath.PRODUCT.PRODUCT_UPLOAD }));
      goBackToHomePage();
    }
  }, [id]);

  // User go back from page after clicking cancel
  const goBackToHomePage = () => {
    navigate('/customers/index');
  };

  useEffect(() => {
    if (!customer.userId && !productData.isEdit) {
      goBackToHomePage();
    }
  }, [customer.userId, productData.isEdit]);

  const handleFormCancel = () => {
    if (!isDirty) {
      dispatch(clearProductDetail());
      goBackToHomePage();
    } else {
      dispatch(openDialog('addProduct'));
    }
  };

  const handleCancelWithReset = () => {
    dispatch(clearImageChanges({ key: uploadPath.PRODUCT.PRODUCT_UPLOAD }));
    handleFormCancel();
  };

  const handleCancelClose = () => dispatch(closeDialog('addProduct'));

  const breadCrumbList = [
    {
      id: '1',
      icon: <LuHouse />,
      pageName: 'Product',
      onClick: handleFormCancel,
    },
  ];

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(async (data) => {
      // console.log('Form data after validation:', data);

      // Clear previous errors
      setImageError('');
      setSerialError('');

      // Validate images for new products
      const hasNewImages = imageFiles?.files?.length > 0 || imageFiles?.length > 0;

      if (!productData.isEdit && !hasNewImages) {
        setImageError('Product image is required.');
        return;
      }

      // For edit mode, check if there are any images left after removals
      if (productData.isEdit) {
        const remainingImages = (images?.length || 0) - (fileData.removedImageUrls?.length || 0);
        if (remainingImages <= 0 && !hasNewImages) {
          setImageError('At least one product image is required.');
          return;
        }
      }

      // Validate serial number uniqueness for new products
      if (!productData.isEdit && serialList.includes(data.serialNo)) {
        setSerialError(
          'This Serial No already exists. Please use a unique Serial No.',
        );
        return;
      }

      const imageChanges = {
        removedImageUrls: fileData.removedImageUrls || [],
      };

      // console.log('Submitting with imageChanges:', imageChanges);

      const selectedSku = data.model[0];
      const matchedModel = models?.find((model) => model.value === selectedSku);

      const inputData = {
        // Use the selected model value from form data instead of productData.skuId
        name: matchedModel ? matchedModel.label : '',
        category: data.productCategory[0],
        subCategory: data.productSubCategory[0],
        CRM_AGENT: 'CRM',
        serialNo: data.serialNo,
        purchaseDate: dateToTimeStamp(data.purchaseDate),
        warrantyDurationInDays: matchedModel ? matchedModel.warrantyPeriodInDays : 0,
        extWarrantyDurationInDays: matchedModel ? matchedModel.extWarrantyPeriodInDays : 0,
        imagePath: uploadPath.PRODUCT.PRODUCT_UPLOAD,
        registrationFlow: data.registrationFlow,
        lastUpdatedDate: dateToTimeStamp(new Date()),
        skuId: data.model[0],
      };

      const createData = {
        customerId: userId,
        createdDate: dateToTimeStamp(new Date()),
        ...(productData.customerEmail && { email: productData.customerEmail }),
      };

      const editData = {
        id: productData.productId,
        userId: userId,
        customerId: userId,
      };

      if (productData.isEdit) {
        // console.log('Dispatching EDIT_PRODUCT');
        dispatch({
          type: 'EDIT_PRODUCT',
          payload: { ...inputData, ...editData, ...imageChanges },
        });
      } else {
        // console.log('Dispatching ADD_NEW_PRODUCT');
        dispatch({
          type: 'ADD_NEW_PRODUCT',
          payload: { ...inputData, ...createData },
        });
      }
      dispatch(clearProductDetail());
      dispatch(clearFiles({ key: uploadPath.PRODUCT.PRODUCT_UPLOAD }));
      dispatch(clearImageChanges({ key: uploadPath.PRODUCT.PRODUCT_UPLOAD }));
    })(e);
  };

  const isEditMode = productData.isEdit;

  const redStar = <span style={{ color: 'red' }}>*</span>;

  // Calculate remaining images for edit mode
  const getRemainingImageCount = () => {
    const currentImages = images?.filter(
      (image) => !fileData?.removedImageUrls?.includes(image.url)
    ).length || 0;
    return currentImages;
  };

  const maxImagesAllowed = 3;
  const remainingSlots = isEditMode ? maxImagesAllowed - getRemainingImageCount() : maxImagesAllowed;

  return (
    <Stack
      spacing={6}
      className={styles.container}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="sm"
    >
      <BreadCrumbBox
        navPageList={breadCrumbList}
        currentPage={productData.isEdit ? 'Edit' : 'Register'}
      />
      <Heading size="xl" className={styles.header}>
        {productData.title || 'Product Registration'}
      </Heading>
      <DialogBox
        dialog_title="Unsaved Changes"
        dialog_body="Are you sure to discard the changes?"
        cancelButtonName="No"
        SaveButtonName="Discard"
        open={cancelFormModalOpen}
        onClose={handleCancelClose}
        saveButtonOnclick={() => {
          dispatch(clearProductDetail());
          goBackToHomePage();
          handleCancelClose();
        }}
      />

      <form onSubmit={handleFormSubmit}>
        <Stack spacing={6}>
          <GridRow>
            <GridCol>
              <SelectBox
                label={
                  <>
                    {'Model'} {redStar}
                  </>
                }
                name="model"
                invalid={!!errors.model}
                errorText={errors.model?.message}
                control={control}
                items={models || []}
                placeholder="All Models"
              />
            </GridCol>

            <GridCol>
              <SelectBox
                label={
                  <>
                    {'Category'} {redStar}
                  </>
                }
                invalid={!!errors.productCategory}
                errorText={errors.productCategory?.message}
                control={control}
                name="productCategory"
                items={productCategories}
              />
            </GridCol>

            <GridCol>
              <SelectBox
                label={
                  <>
                    {'Sub Category'} {redStar}
                  </>
                }
                invalid={!!errors.productSubCategory}
                errorText={errors.productSubCategory?.message}
                control={control}
                name="productSubCategory"
                disabled={!selectedCategory || selectedCategory.length === 0}
                items={
                  selectedCategory && selectedCategory.length > 0
                    ? productSubcategories[selectedCategory[0]] || []
                    : []
                }
              />
            </GridCol>
          </GridRow>

          <GridRow>
            <GridCol base={12} sm={6} md={4}>
              <DatePickerBox
                control={control}
                label={
                  <>
                    {'Purchase Date'} {redStar}
                  </>
                }
                invalid={!!errors.purchaseDate}
                errorText={errors.purchaseDate?.message}
                name="purchaseDate"
              />
            </GridCol>

            <GridCol base={12} sm={6} md={4}>
              <InputBox
                label={
                  <>
                    {'Serial No'} {redStar}
                  </>
                }
                invalid={!!errors.serialNo || !!serialError}
                errorText={errors.serialNo?.message || serialError}
                register={{ ...register('serialNo') }}
                disabled={
                  productData?.isEdit &&
                  productData?.registrationFlow === 'QRCODE'
                }
              />
            </GridCol>

            <GridCol base={12} sm={6} md={4}>
              <InputBox
                label={'Warranty Period (Days):'}
                value={selectedModel ? String(selectedModel.warrantyPeriodInDays ?? 0) : ''}
                disabled={true}
              />
            </GridCol>

            <GridCol base={12} sm={6} md={4}>
              <InputBox
                label={'Extended Warranty Period (Days):'}
                value={selectedModel ? String(selectedModel.extWarrantyPeriodInDays ?? 0) : ''}
                disabled={true}
              />
            </GridCol>
          </GridRow>

          {/* Enhanced Image Section */}
          <GridRow>
            <GridCol>
              <Box
                p={6}
                bg={imageSectionBg}
                borderRadius="xl"
                border="1px solid"
                borderColor="purple.100"
              >
                <HStack mb={6} spacing={2}>
                  <Text fontSize="lg">📷</Text>
                  <VStack align="start" spacing={0}>
                    <Heading
                      size="md"
                      color={imageSectionTextColor}
                      fontWeight="600"
                    >
                      Product Images
                    </Heading>
                    <Text fontSize="sm" color="purple.500">
                      Visual documentation of the product
                    </Text>
                  </VStack>
                </HStack>

                {imagesLoading ? (
                  <Flex
                    justify="center"
                    align="center"
                    py={12}
                    direction="column"
                    spacing={4}
                  >
                    <Spinner size="xl" color="purple.500" thickness="4px" />
                    <Text mt={4} color={imageSectionTextColor} fontWeight="500">
                      Loading images...
                    </Text>
                  </Flex>
                ) : imagesError ? (
                  <Alert
                    status="error"
                    borderRadius="lg"
                    bg="red.50"
                    border="1px solid"
                    borderColor="red.200"
                  >
                    <Text color="red.700">{imagesError}</Text>
                  </Alert>
                ) : (
                  <VStack align="start" spacing={6}>
                    {images && images.length > 0 && (
                      <SimpleGrid
                        columns={{ base: 2, md: 3, lg: 4 }}
                        spacing={4}
                        w="full"
                      >
                        {images
                          .filter(
                            (image) =>
                              !fileData?.removedImageUrls?.includes(image.url),
                          )
                          .map((image, index) => (
                            <Box
                              key={image.url || index}
                              position="relative"
                              borderRadius="xl"
                              overflow="hidden"
                              border="2px solid"
                              borderColor="purple.200"
                              bg={cardBg}
                              _hover={{
                                borderColor: 'purple.400',
                                transform: 'translateY(-4px)',
                                boxShadow:
                                  '0 8px 25px rgba(128, 90, 213, 0.15)',
                              }}
                              transition="all 0.3s ease"
                              cursor="pointer"
                              onClick={() => handleImageClick(image)}
                            >
                              <Image
                                src={image.url}
                                alt={image.name || `Product image ${index + 1}`}
                                width="100%"
                                height="120px"
                                objectFit="cover"
                              />
                              <Box
                                position="absolute"
                                bottom={0}
                                left={0}
                                right={0}
                                bg="linear-gradient(transparent, rgba(0,0,0,0.7))"
                                p={2}
                              >
                                <Text
                                  fontSize="xs"
                                  fontWeight="500"
                                  isTruncated
                                  color="white"
                                >
                                  {image.name || `Image ${index + 1}`}
                                </Text>
                              </Box>
                            </Box>
                          ))}
                      </SimpleGrid>
                    )}

                    {isEditMode && remainingSlots > 0 && (
                      <Box w="full">
                        <ImageUploader
                          isEdit={productData.isEdit}
                          path={uploadPath.PRODUCT.PRODUCT_UPLOAD}
                          existingImages={images || []}
                          showExistingImages={true}
                          maxImages={remainingSlots}
                          // onRemoveExisting={(removedImage, index) => {
                          //   console.log(
                          //     'Removed existing image:',
                          //     removedImage,
                          //     'at index:',
                          //     index,
                          //   );
                          // }}
                        />
                      </Box>
                    )}

                    {!isEditMode && (
                      <Box w="full">
                        <ImageUploader
                          isEdit={productData.isEdit}
                          path={uploadPath.PRODUCT.PRODUCT_UPLOAD}
                          label="Product Image"
                          maxImages={maxImagesAllowed}
                        />
                      </Box>
                    )}

                    {(!images || images.length === 0) && !imageFiles?.length && isEditMode && (
                      <Box
                        w="full"
                        p={8}
                        textAlign="center"
                        border="2px dashed"
                        borderColor="purple.200"
                        borderRadius="xl"
                        bg={cardBg}
                      >
                        <Text fontSize="4xl" mb={2}>
                          📷
                        </Text>
                        <Text color="purple.600" fontWeight="500">
                          No images uploaded yet
                        </Text>
                        <Text color="purple.500" fontSize="sm">
                          Images help provide better context for the product
                        </Text>
                      </Box>
                    )}
                  </VStack>
                )}
              </Box>
              {imageError && (
                <Text
                  color="red.400"
                  fontWeight="semibold"
                  pt={2}
                  fontSize="xs"
                >
                  {imageError}
                </Text>
              )}
            </GridCol>
          </GridRow>

          {/* Submit Button */}
          <Flex justify="flex-end" gap={4}>
            <ButtonBox
              name="Cancel"
              variant="plain"
              onClick={handleCancelWithReset}
            />
            <ButtonBox type="submit" name="Submit" />
          </Flex>
        </Stack>
      </form>

      {/* Image Preview Dialog */}
      <ImagePreviewDialog
        isOpen={isImageDialogOpen}
        onOpenChange={(details) => {
          setIsImageDialogOpen(details.open);
          if (!details.open) setSelectedImage(null);
        }}
        selectedImage={selectedImage}
        title={selectedImage?.name}
        defaultTitle="Product Image"
        width="60vw"
        height="60vh"
      />
    </Stack>
  );
};

export default ProductAdd;